import React, { useEffect, useState } from 'react';
import { FileCheck2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import InfoTip from './InfoTip';

const KnowledgeUploadSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    axiosInstance.get('/knowledge/intake/config')
      .then(({ data }) => { if (active) setConfig(data); })
      .catch((error) => toast.error(error.response?.data?.detail || 'Failed to load automated upload settings'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const persist = async (changes) => {
    if (!config) return;
    const previous = config;
    const next = { ...config, ...changes };
    if (changes.knowledge_intake_enabled === false) next.locked_upload_mode = null;
    if (next.locked_upload_mode === 'auto') next.knowledge_intake_enabled = true;
    setConfig(next);
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/knowledge/intake/config', {
        knowledge_intake_enabled: Boolean(next.knowledge_intake_enabled),
        automation_mode: next.automation_mode || 'suggest_and_review',
        locked_upload_mode: next.locked_upload_mode || null,
        fields: next.fields || [],
        version_matching_enabled: next.version_matching_enabled !== false,
      });
      setConfig(data);
      toast.success('Automated upload settings saved');
    } catch (error) {
      setConfig(previous);
      toast.error(error.response?.data?.detail || 'Automated upload settings could not be saved');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500"><Loader2 size={17} className="animate-spin" /> Loading automated upload settings…</Card>;
  }

  if (!config) return null;

  return (
    <Card className="overflow-hidden border-slate-200" data-testid="knowledge-upload-settings">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 p-6">
        <div className="flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900"><Sparkles size={20} className="text-sky-600" aria-hidden="true" /> Automated upload</h2>
          <InfoTip label="About automated upload">Control whether this organisation can use Upload and auto-classify. Users review suggestions before anything is published.</InfoTip>
        </div>
        <Badge aria-live="polite" className={config.knowledge_intake_enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'}>
          {saving ? 'Saving…' : config.knowledge_intake_enabled ? 'Enabled' : 'Standard upload only'}
        </Badge>
      </div>

      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-900">Allow Auto-classify upload</p>
            <InfoTip label="About Auto-classify upload">When enabled, Knowledge Hub offers Standard upload and Auto-classify from the same Upload knowledge button.</InfoTip>
          </div>
          <Switch
            checked={Boolean(config.knowledge_intake_enabled)}
            onCheckedChange={(checked) => persist({ knowledge_intake_enabled: checked })}
            disabled={saving}
            aria-label="Allow Auto-classify upload"
            data-testid="knowledge-intake-enabled-switch"
          />
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <div className="text-sm">
            <div className="flex items-center gap-2">
              <label htmlFor="upload-choice" className="font-semibold text-slate-800">Organisation upload choice</label>
              <InfoTip label="About organisation upload choice">A locked choice is enforced by the server and cannot be bypassed from Knowledge Hub.</InfoTip>
            </div>
            <select
              id="upload-choice"
              value={config.locked_upload_mode || ''}
              onChange={(event) => persist({ locked_upload_mode: event.target.value || null })}
              disabled={saving}
              className="mt-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 font-normal disabled:cursor-wait disabled:bg-slate-100"
            >
              <option value="">Let each user choose</option>
              <option value="standard">Standard upload only</option>
              <option value="auto">Auto-classify only</option>
            </select>
          </div>

          <div className="text-sm">
            <div className="flex items-center gap-2">
              <label htmlFor="review-level" className="font-semibold text-slate-800">Auto-classify review level</label>
              <InfoTip label="About review level">Access is always confirmed and publishing remains a deliberate user action.</InfoTip>
            </div>
            <select
              id="review-level"
              value={config.automation_mode || 'suggest_and_review'}
              onChange={(event) => persist({ automation_mode: event.target.value })}
              disabled={!config.knowledge_intake_enabled || saving}
              className="mt-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 font-normal disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              <option value="manual">Review every field</option>
              <option value="suggest_and_review">Suggest and require review</option>
              <option value="auto_publish_safe">Auto-accept safe fields, then review</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <FileCheck2 size={15} className="shrink-0" aria-hidden="true" />
          <span>Changes save immediately.</span>
          <InfoTip label="About how upload settings apply">When this setting is off, users keep the existing standard uploader. Auto-classify only opens the assisted upload directly; letting users choose shows both options.</InfoTip>
        </div>
      </div>
    </Card>
  );
};

export default KnowledgeUploadSettings;
